import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon, EyeIcon, ClockIcon } from "@heroicons/react/24/outline";
import { PageLoader, Button, TabNavigation } from "../../ui";
import { FormModal, FormModalHeader, FormModalBody, FormModalField, FormModalFooter, formInputCls } from "../../ui/FormModal";
import VaptStatusBadge from "../components/VaptStatusBadge";
import { DataTable } from "../../common/DataTable";
import {
  listVaptScansApi, listVaptWorkersApi, getVaptToolsStatusApi, listVaptScanTemplatesApi,
  listVaptSchedulesApi, createVaptScheduleApi, setVaptScheduleEnabledApi, listVaptProjectsApi,
} from "../../../services/axios/AuthApis/VAPTApis";
import { VAPT_SCAN_STATUSES, VAPT_DOMAINS } from "../utils/vaptConstants";

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

const TOOL_STATUS_STYLES = {
  Ready: "text-green-700 bg-green-50",
  "Not Installed": "text-red-700 bg-red-50",
  "Not Configured": "text-amber-700 bg-amber-50",
};

// Display-only labels for the tool availability status returned by the API.
const TOOL_STATUS_LABELS = {
  Ready: "Ready",
  "Not Installed": "Unavailable",
  "Not Configured": "Configuration required",
};

const VaptScans = () => {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [toolStatus, setToolStatus] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [section, setSection] = useState("jobs");
  const [statusFilter, setStatusFilter] = useState("");
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ project: "", domain: "", frequency: "Daily", start_at: "" });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const loadSchedules = () => {
    listVaptSchedulesApi().then((rows) => setSchedules(asArray(rows))).catch(() => setSchedules([]));
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      listVaptScansApi(), listVaptWorkersApi(), getVaptToolsStatusApi(), listVaptScanTemplatesApi(),
      listVaptSchedulesApi(), listVaptProjectsApi(),
    ])
      .then(([s, w, ts, tpl, sch, proj]) => {
        setScans(asArray(s).map((sc) => ({ ...sc, id: sc.scan_id })));
        setWorkers(asArray(w));
        setToolStatus(asArray(ts));
        setTemplates(asArray(tpl));
        setSchedules(asArray(sch));
        setProjects(asArray(proj));
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load scan manager data.");
        setLoading(false);
      });
  }, []);

  const submitSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleForm.project || !scheduleForm.start_at) return;
    setSavingSchedule(true);
    try {
      await createVaptScheduleApi({
        project: scheduleForm.project,
        domain: scheduleForm.domain || undefined,
        frequency: scheduleForm.frequency,
        start_at: scheduleForm.start_at,
      });
      setShowScheduleModal(false);
      setScheduleForm({ project: "", domain: "", frequency: "Daily", start_at: "" });
      loadSchedules();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to create schedule.");
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleSchedule = async (schedule) => {
    try {
      await setVaptScheduleEnabledApi(schedule.schedule_id, !schedule.enabled);
      loadSchedules();
    } catch {
      setError("Failed to update schedule.");
    }
  };

  const uniqueProjects = useMemo(
    () => [...new Set(scans.map((s) => s.project_name || s.project_display_id).filter(Boolean))],
    [scans],
  );
  const uniqueDomains = useMemo(
    () => [...new Set(scans.map((s) => s.scan_type).filter(Boolean))],
    [scans],
  );

  const scanColumns = [
    {
      id: "scan_id",
      accessorFn: (r) => r.scan_id,
      header: "Scan ID",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Scan ID", placeholder: "Search scan ID" } },
      cell: ({ row }) => <span>{row.original.scan_id}</span>,
    },
    {
      id: "project",
      accessorFn: (r) => r.project_name || r.project_display_id,
      header: "Project",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Project",
          placeholder: "All projects",
          options: uniqueProjects.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.project_name || row.original.project_display_id || "Asset VAPT Project"}</span>,
    },
    {
      id: "scan_type",
      accessorFn: (r) => r.scan_type,
      header: "Domain",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Domain",
          placeholder: "All domains",
          options: uniqueDomains.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.scan_type ?? "—"}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: VAPT_SCAN_STATUSES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
    },
    {
      id: "start_time",
      accessorFn: (r) => r.start_time,
      header: "Start Time",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.start_time ? new Date(row.original.start_time).toLocaleString() : "-"}</span>,
    },
    {
      id: "duration",
      header: "Duration",
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        if (!r.start_time || !r.end_time) return <span>—</span>;
        const seconds = Math.max(0, Math.round((new Date(r.end_time) - new Date(r.start_time)) / 1000));
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return <span>{`${m}m ${s}s`}</span>;
      },
    },
    {
      id: "vulnerabilities_found",
      accessorFn: (r) => r.vulnerabilities_found,
      header: "Findings",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.vulnerabilities_found ?? "—"}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/ai-vapt/scans/${row.original.scan_id}`);
          }}
          title="View scan details"
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <EyeIcon className="w-4 h-4" />
        </button>
      ),
    },
  ];

  const uniqueScheduleProjects = [...new Set(schedules.map((s) => s.project_name || s.project_display_id).filter(Boolean))];
  const uniqueFrequencies = [...new Set(schedules.map((s) => s.frequency).filter(Boolean))];

  const scheduleColumns = [
    {
      id: "schedule_id",
      accessorFn: (r) => r.schedule_id,
      header: "Schedule",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Schedule", placeholder: "Search schedule" } },
      cell: ({ row }) => <span>{row.original.schedule_id}</span>,
    },
    {
      id: "project",
      accessorFn: (r) => r.project_name || r.project_display_id,
      header: "Project",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Project",
          placeholder: "All projects",
          options: uniqueScheduleProjects.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.project_name || row.original.project_display_id || "—"}</span>,
    },
    {
      id: "frequency",
      accessorFn: (r) => r.frequency,
      header: "Frequency",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Frequency",
          placeholder: "All frequencies",
          options: uniqueFrequencies.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.frequency}</span>,
    },
    {
      id: "next_run",
      accessorFn: (r) => r.next_run,
      header: "Next Run",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.next_run ? new Date(row.original.next_run).toLocaleString() : "—"}</span>,
    },
    {
      id: "last_run",
      accessorFn: (r) => r.last_run,
      header: "Last Run",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.last_run ? new Date(row.original.last_run).toLocaleString() : "Never"}</span>,
    },
    {
      id: "enabled",
      accessorFn: (r) => (r.enabled ? "enabled" : "disabled"),
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: [
            { value: "enabled", label: "Enabled" },
            { value: "disabled", label: "Disabled" },
          ],
        },
      },
      cell: ({ row }) => (
        <span className={`text-xs font-semibold ${row.original.enabled ? "text-green-700" : "text-gray-400"}`}>
          {row.original.enabled ? "Enabled" : "Disabled"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleSchedule(row.original);
          }}
          className="text-xs font-semibold text-primary-600 hover:text-primary-700 underline"
        >
          {row.original.enabled ? "Disable" : "Enable"}
        </button>
      ),
    },
  ];

  const uniqueToolDomains = [...new Set(toolStatus.flatMap((t) => t.domain || []))];

  const toolColumns = [
    {
      id: "name",
      accessorFn: (r) => r.name,
      header: "Tool",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Tool", placeholder: "Search tool" } },
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      id: "domain",
      accessorFn: (r) => (r.domain || []).join(", "),
      header: "Domain(s)",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Domain",
          placeholder: "All domains",
          options: uniqueToolDomains.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{(row.original.domain || []).join(", ") || "—"}</span>,
    },
    {
      id: "version",
      accessorFn: (r) => r.version,
      header: "Version",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.version || "—"}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: Object.keys(TOOL_STATUS_LABELS).map((v) => ({ value: v, label: TOOL_STATUS_LABELS[v] })),
        },
      },
      cell: ({ row }) => (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TOOL_STATUS_STYLES[row.original.status] || "text-gray-500 bg-gray-50"}`}>
          {TOOL_STATUS_LABELS[row.original.status] || row.original.status}
        </span>
      ),
    },
    {
      id: "enabled",
      accessorFn: (r) => (r.enabled ? "yes" : "no"),
      header: "Enabled",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Enabled",
          placeholder: "All",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ],
        },
      },
      cell: ({ row }) => (
        <span className={`text-xs font-semibold ${row.original.enabled ? "text-green-700" : "text-gray-400"}`}>
          {row.original.enabled ? "Yes" : "No"}
        </span>
      ),
    },
    {
      id: "notes",
      header: "Notes",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.notes || (row.original.requires_auth ? "Requires authentication/license" : "—")}</span>,
    },
  ];

  const uniqueWorkerRegions = [...new Set(workers.map((w) => w.region).filter(Boolean))];
  const uniqueWorkerStatuses = [...new Set(workers.map((w) => w.status).filter(Boolean))];

  const workerColumns = [
    {
      id: "name",
      accessorFn: (r) => r.name,
      header: "Worker",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Worker", placeholder: "Search worker" } },
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      id: "region",
      accessorFn: (r) => r.region,
      header: "Region",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Region",
          placeholder: "All regions",
          options: uniqueWorkerRegions.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.region}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: uniqueWorkerStatuses.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
    },
  ];

  if (loading) return <PageLoader title="Loading scan manager…" />;

  const sections = [
    { id: "jobs", name: "Scan Jobs" },
    { id: "schedules", name: "Schedules" },
    { id: "templates", name: "Templates" },
    { id: "tools", name: "Tools" },
    { id: "workers", name: "Workers" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Scan Manager</h2>
          <p className="text-sm text-gray-500">Manage scan jobs, schedules, templates, tools, and worker capacity</p>
        </div>
        <div className="flex items-center gap-2">
          {section === "schedules" && (
            <Button variant="secondary" icon={ClockIcon} onClick={() => setShowScheduleModal(true)}>
              New Schedule
            </Button>
          )}
          <Button icon={PlusIcon} onClick={() => navigate("/ai-vapt/scans/new")}>
            New Scan
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5">{error}</div>
      )}

      <TabNavigation tabs={sections} activeTab={section} onTabChange={setSection} boxedActive />

      {section === "jobs" && (
        <div className="space-y-4">
          <DataTable
            columns={scanColumns}
            data={scans}
            getRowId={(r) => String(r.scan_id)}
            loading={loading}
            emptyMessage="No scans yet."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}

      {section === "schedules" && (
        <div className="space-y-3">
          <p className="text-xs text-gray-400">
            Recurring scans — each due, enabled schedule automatically creates a scan through the same launch and
            validation path as a manual scan.
          </p>
          <DataTable
            columns={scheduleColumns}
            data={schedules}
            getRowId={(s) => String(s.schedule_id)}
            emptyMessage="No scheduled scans yet."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}

      {section === "templates" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.config_id} className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-900">{t.name}</p>
              <p className="text-xs text-gray-500 mt-1">{t.description}</p>
              <p className="text-xs text-gray-600 mt-2">
                Tools: {(t.scanning_tools || []).join(", ") || "—"}
              </p>
              {t.scan_type && (
                <span className="inline-block mt-3 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  {t.scan_type}
                </span>
              )}
            </div>
          ))}
          {templates.length === 0 && (
            <p className="text-sm text-gray-400 col-span-full">
              No scan templates are available. You can select tools manually when creating a scan.
            </p>
          )}
        </div>
      )}

      {section === "tools" && (
        <div className="space-y-3">
          <p className="text-xs text-gray-400">
            Live tool availability check — each tool is verified on every load. A tool is marked Ready only when it was
            found and responded to a version check, never solely because it is enabled in the registry.
          </p>
          <DataTable
            columns={toolColumns}
            data={toolStatus}
            getRowId={(t) => String(t.tool_id)}
            emptyMessage="No tools are registered in the tool registry."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}

      {section === "workers" && (
        <div className="space-y-3">
          {/*
            No Worker/ScanWorker model exists in the current VAPT backend —
            there is no fleet of distributed scan execution workers to query.
            Do not fabricate worker rows; show an explicit gap state instead.
          */}
          <p className="text-sm text-gray-400">
            Distributed scan workers are not enabled in this environment.
          </p>
          <DataTable
            columns={workerColumns}
            data={workers}
            getRowId={(w, index) => String(w.id || w.name || index)}
            emptyMessage="No worker data available."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}

      <FormModal open={showScheduleModal} onClose={() => setShowScheduleModal(false)} maxWidth="md">
        <FormModalHeader
          icon={ClockIcon}
          title="New Schedule"
          subtitle="Recurring scan — automatically created and launched on the configured schedule."
          onClose={() => setShowScheduleModal(false)}
        />
        <FormModalBody id="vapt-schedule-form" onSubmit={submitSchedule}>
          <FormModalField label="Project" required>
            <select
              className={formInputCls(false)}
              value={scheduleForm.project}
              onChange={(e) => setScheduleForm({ ...scheduleForm, project: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {projects.map((p) => (
                <option key={p.project_id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FormModalField>
          <FormModalField label="Domain">
            <select
              className={formInputCls(false)}
              value={scheduleForm.domain}
              onChange={(e) => setScheduleForm({ ...scheduleForm, domain: e.target.value })}
            >
              <option value="">Project default</option>
              {VAPT_DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </FormModalField>
          <div className="grid grid-cols-2 gap-4">
            <FormModalField label="Frequency" required>
              <select
                className={formInputCls(false)}
                value={scheduleForm.frequency}
                onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
                required
              >
                <option value="One Time">One Time</option>
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </FormModalField>
            <FormModalField label="Start At" required>
              <input
                type="datetime-local"
                className={formInputCls(false)}
                value={scheduleForm.start_at}
                onChange={(e) => setScheduleForm({ ...scheduleForm, start_at: e.target.value })}
                required
              />
            </FormModalField>
          </div>
        </FormModalBody>
        <FormModalFooter formId="vapt-schedule-form" onCancel={() => setShowScheduleModal(false)} saving={savingSchedule} submitLabel="Schedule" />
      </FormModal>
    </div>
  );
};

export default VaptScans;
